import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Blocco } from '@/api/types';
import { Card, CardGrid } from '@/ui/Card/Card';
import styles from './BlocchiRenderer.module.css';

/**
 * Il renderer dei blocchi tipizzati (decisioni 29 e 40).
 * ---------------------------------------------------------------------------
 * Ogni tipo del registro (`backend/apps/contenuti/blocchi.py`) ha QUI il suo
 * componente. È il patto che rende verificabile il vincolo WCAG AA: si
 * certifica il componente una volta, non ogni pagina che la redazione scrive.
 *
 * Un tipo SCONOSCIUTO (backend più nuovo del frontend) non viene saltato in
 * silenzio: viene reso come un riquadro che dichiara il buco. Un blocco che
 * sparisce senza dirlo è contenuto perso senza che nessuno se ne accorga —
 * e la promessa di dichiararlo sta scritta nel registro lato backend.
 *
 * Note di accessibilità per tipo:
 *  · `titolo`     — livelli 2..4 dal contratto; il livello 1 è l'h1 della
 *                   pagina e non è un blocco (WCAG 1.3.1).
 *  · `nota`       — `role="note"`; la variante "avviso" NON è segnalata dal
 *                   solo colore: c'è il prefisso testuale (WCAG 1.4.1).
 *  · `immagine`   — alt obbligatorio dal contratto; fonte e licenza rese in
 *                   `<figcaption>`: il vincolo 5 arriva fino al pixel.
 *  · `tabella`    — `<caption>` obbligatoria dal contratto, `scope="col"`.
 *  · `faq`        — `<details>/<summary>`: espandi/comprimi nativo, gratis
 *                   per tastiera e screen reader, nessun ARIA da mantenere.
 *  · `video`      — MAI un iframe di terzi: la CSP li blocca (`default-src
 *                   'self'`). File interno → `<video controls>`; URL esterno
 *                   → link esplicito. Un video senza sottotitoli dichiarati
 *                   non si incorpora comunque alla cieca (WCAG 1.2.2).
 */

/**
 * `superficie`: su quale fondo i blocchi vengono resi. I blocchi di testo
 * ereditano i colori dal template; i componenti con SUPERFICIE PROPRIA
 * (le card di `griglia_card`) no — su un template scuro una card bianca
 * col testo ereditato bianco è contenuto invisibile (WCAG 1.4.3, residui
 * voce 30). Con `superficie="scura"` la card prende la variante scura,
 * che LEGA fondo e colori del testo nella stessa classe.
 */
export type SuperficieBlocchi = 'chiara' | 'scura';

export function BlocchiRenderer({
  blocchi,
  superficie = 'chiara',
}: {
  readonly blocchi: readonly Blocco[];
  readonly superficie?: SuperficieBlocchi;
}) {
  return (
    <>
      {blocchi.map((blocco, indice) => (
        // L'indice come chiave è corretto qui: la lista è immutabile dentro
        // una risposta e non viene riordinata lato client.
        <BloccoView key={indice} blocco={blocco} superficie={superficie} />
      ))}
    </>
  );
}

function BloccoView({
  blocco,
  superficie,
}: {
  readonly blocco: Blocco;
  readonly superficie: SuperficieBlocchi;
}) {
  const { t } = useTranslation();

  switch (blocco.tipo) {
    case 'titolo': {
      const Heading = `h${blocco.livello}` as 'h2' | 'h3' | 'h4';
      return <Heading className={styles[`titolo${blocco.livello}`]}>{blocco.testo}</Heading>;
    }

    case 'prosa':
      return <p className={styles.prosa}>{blocco.testo}</p>;

    case 'elenco': {
      const Lista = blocco.ordinato ? 'ol' : 'ul';
      return (
        <Lista className={styles.elenco}>
          {blocco.voci.map((voce, i) => (
            <li key={i}>{voce}</li>
          ))}
        </Lista>
      );
    }

    case 'cta': {
      const esterno = blocco.url.startsWith('http');
      /* `mailto:` e `tel:` sono ammessi SOLO sul blocco cta (coda rimisura
         31/07/2026, es. «Attiva il tuo Comune» su /progetto/pilota/): sono
         ancore native, non rotte del router, e sul totem restano inerti
         (KioskProvider blocca solo gli schemi web esterni). */
      const contatto = blocco.url.startsWith('mailto:') || blocco.url.startsWith('tel:');
      return (
        /* `data-blocco`: gancio SENZA classi hashate per i template che
           devono stilare le coppie di cta (es. pieno + contorno sul
           trattamento scuro di /progetto/pilota/, residui voce 25). */
        <p className={styles.ctaRiga} data-blocco="cta">
          {esterno ? (
            <a
              href={blocco.url}
              className={styles.cta}
              rel="noopener noreferrer"
              data-external="true"
            >
              {blocco.etichetta}
            </a>
          ) : contatto ? (
            <a href={blocco.url} className={styles.cta}>
              {blocco.etichetta}
            </a>
          ) : (
            <Link to={blocco.url} className={styles.cta}>
              {blocco.etichetta}
            </Link>
          )}
        </p>
      );
    }

    case 'nota':
      return (
        <div
          role="note"
          className={`${styles.nota} ${blocco.variante === 'avviso' ? styles.notaAvviso : ''}`}
        >
          {/* Il prefisso è testo, non solo colore (WCAG 1.4.1). */}
          <strong>
            {blocco.variante === 'avviso'
              ? t('block_warning_prefix', 'Attenzione')
              : t('block_note_prefix', 'Nota')}
            {': '}
          </strong>
          {blocco.testo}
        </div>
      );

    case 'immagine':
      return (
        <figure className={styles.figura}>
          <img src={blocco.url} alt={blocco.alt} loading="lazy" />
          <figcaption>
            {blocco.didascalia ? <span>{blocco.didascalia} — </span> : null}
            {/* Attribuzione obbligatoria (decisione 37, vincolo 5). */}
            <span className={styles.attribuzione}>
              {t('render_cammini_fonte', 'Fonte')}: {blocco.fonte} · {blocco.licenza}
            </span>
          </figcaption>
        </figure>
      );

    case 'tabella':
      return (
        <div className={styles.tabellaScroll} tabIndex={0} role="group" aria-label={blocco.didascalia}>
          <table className={styles.tabella}>
            {/* `didascalia_nascosta` (coda rimisura 31/07/2026, voce 20): la
                caption resta SEMPRE nel DOM — è il nome accessibile (WCAG
                1.3.1) — ma sparisce visivamente quando l'importatore l'ha
                ricavata dall'h2 di sezione: mostrarla la farebbe leggere
                due volte. */}
            <caption className={blocco.didascalia_nascosta ? 'ic-sr-only' : undefined}>
              {blocco.didascalia}
            </caption>
            <thead>
              <tr>
                {blocco.intestazioni.map((testata, i) => (
                  <th key={i} scope="col">
                    {testata}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {blocco.righe.map((riga, i) => (
                <tr key={i}>
                  {riga.map((cella, j) => (
                    <td key={j}>{cella}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'faq':
      return (
        <div className={styles.faq}>
          {blocco.voci.map((voce, i) => (
            <details key={i} className={styles.faqVoce}>
              <summary>{voce.domanda}</summary>
              <p>{voce.risposta}</p>
            </details>
          ))}
        </div>
      );

    case 'accordion':
      /* `accordion` (voce 32): come `faq` — <details>/<summary> nativi,
         espandi/comprimi gratis per tastiera e screen reader — ma col corpo
         STRUTTURATO: renderer ricorsivo sugli stessi blocchi del registro.
         Il contratto vieta l'annidamento (lo rifiuta il backend); la
         `superficie` si propaga, così le card dentro un elemento su fascia
         scura mantengono il contrasto (voce 30). */
      return (
        <div
          className={[
            styles.accordion,
            superficie === 'scura' ? styles.accordionScuro : null,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {blocco.elementi.map((elemento, i) => (
            <details key={i} className={styles.accordionVoce}>
              <summary>
                {elemento.titolo}
                {/* Voce 41: il distintivo è un badge ACCANTO al titolo, mai
                    al suo posto — il nome accessibile resta titolo+badge. */}
                {elemento.distintivo ? (
                  <span className={styles.distintivoAccordion}>{elemento.distintivo}</span>
                ) : null}
              </summary>
              <div className={styles.accordionCorpo}>
                <BlocchiRenderer blocchi={elemento.blocchi} superficie={superficie} />
              </div>
            </details>
          ))}
        </div>
      );

    case 'statistiche':
      /* Voce 41: la fascia numerica IN RIGA del riferimento — valore grande
         + etichetta piccola. NON sono card: è una lista di misure. */
      return (
        <ul
          className={[
            styles.statistiche,
            superficie === 'scura' ? styles.statisticheScure : null,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {blocco.voci.map((voce, i) => (
            <li key={i} className={styles.statVoce}>
              <span className={styles.statValore}>{voce.valore}</span>
              {voce.etichetta ? (
                <span className={styles.statEtichetta}>{voce.etichetta}</span>
              ) : null}
            </li>
          ))}
        </ul>
      );

    case 'chip':
      /* Voce 41: etichette brevi affiancate in maiuscoletto — NON un
         elenco puntato (per le frasi c'è `elenco`). */
      return (
        <ul
          className={[styles.chips, superficie === 'scura' ? styles.chipsScure : null]
            .filter(Boolean)
            .join(' ')}
        >
          {blocco.voci.map((voce, i) => (
            <li key={i} className={styles.chipVoce}>
              {voce}
            </li>
          ))}
        </ul>
      );

    case 'citazione':
      return (
        <blockquote className={styles.citazione}>
          <p>{blocco.testo}</p>
          {blocco.autore ? <footer>— {blocco.autore}</footer> : null}
        </blockquote>
      );

    case 'video': {
      // La CSP è `default-src 'self'`: un iframe di terzi non caricherebbe.
      const interno = blocco.url.startsWith('/');
      if (interno) {
        return (
          /* eslint-disable-next-line jsx-a11y/media-has-caption --
             i sottotitoli non sono nel contratto del blocco: finché non ci
             sono, il titolo esplicito e i controlli nativi sono il minimo. */
          <video controls className={styles.video} aria-label={blocco.titolo}>
            <source src={blocco.url} />
          </video>
        );
      }
      return (
        <p className={styles.ctaRiga}>
          <a
            href={blocco.url}
            className={styles.cta}
            rel="noopener noreferrer"
            data-external="true"
          >
            {t('block_video_watch', 'Guarda il video')}: {blocco.titolo}
          </a>
        </p>
      );
    }

    case 'griglia_card':
      return (
        <CardGrid label={t('block_cards_label', 'Collegamenti')} className={styles.griglia}>
          {blocco.card.map((card, i) => (
            <li key={i}>
              {/* voce 30: sulla superficie scura la card prende la variante
                  scura — fondo navy E testo chiaro nella STESSA classe, così
                  non possono divergere. */}
              <Card
                title={card.titolo}
                {...(card.url ? { to: card.url } : {})}
                headingLevel={3}
                {...(superficie === 'scura' ? { className: styles.cardScura } : {})}
              >
                {card.testo ? <p>{card.testo}</p> : null}
              </Card>
            </li>
          ))}
        </CardGrid>
      );

    case 'galleria':
      /* Contratto §22. Una LISTA di figure, non un carosello: un carosello
         è un widget con stato, tastiera e annunci da mantenere, e qui il
         contenuto sono le foto. L'attribuzione è per immagine, sempre
         visibile (vincolo 5). */
      return (
        <figure className={styles.galleria}>
          <ul className={styles.galleriaGriglia}>
            {blocco.immagini.map((immagine, i) => (
              <li key={i} className={styles.galleriaVoce}>
                <img src={immagine.url} alt={immagine.alt} loading="lazy" />
                <span className={styles.attribuzione}>
                  {immagine.didascalia ? <span>{immagine.didascalia} — </span> : null}
                  {t('render_cammini_fonte', 'Fonte')}: {immagine.fonte} · {immagine.licenza}
                </span>
              </li>
            ))}
          </ul>
          {blocco.didascalia ? <figcaption>{blocco.didascalia}</figcaption> : null}
        </figure>
      );

    case 'mappa':
      /* La mappa interattiva (Leaflet) è un chunk da ~150 KB e vive nelle
         rotte che ce l'hanno già. Qui si rende la resa STATICA e accessibile:
         il titolo, l'elenco dei punti con le coordinate e i link. È il
         contenuto vero — una mappa senza elenco è inaccessibile comunque
         (WCAG 1.1.1), quindi l'elenco non è un ripiego: è la versione
         testuale che il blocco deve avere in ogni caso. */
      return (
        <section className={styles.mappaBlocco} aria-label={blocco.titolo}>
          <h3 className={styles.mappaTitolo}>{blocco.titolo}</h3>
          {blocco.punti && blocco.punti.length > 0 ? (
            <ul className={styles.elenco}>
              {blocco.punti.map((punto, i) => (
                <li key={i}>
                  {punto.url ? (
                    punto.url.startsWith('http') ? (
                      <a href={punto.url} rel="noopener noreferrer" data-external="true">
                        {punto.etichetta}
                      </a>
                    ) : (
                      <Link to={punto.url}>{punto.etichetta}</Link>
                    )
                  ) : (
                    punto.etichetta
                  )}{' '}
                  <span className={styles.attribuzione}>
                    ({punto.lat.toFixed(5)}, {punto.lon.toFixed(5)})
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.attribuzione}>
              {t('block_map_center', 'Centro')}: {blocco.centro_lat.toFixed(5)},{' '}
              {blocco.centro_lon.toFixed(5)}
            </p>
          )}
          {blocco.didascalia ? <p className={styles.prosa}>{blocco.didascalia}</p> : null}
        </section>
      );

    case 'embed':
      /* MAI un iframe: la CSP del portale è `default-src 'self'` senza
         `frame-src` (docker/nginx/intestazioni-sicurezza.conf), quindi un
         iframe di terzi resterebbe un riquadro bianco. Un riquadro bianco è
         una bugia; un link dichiarato con la fonte è il contenuto. Stessa
         scelta già presa per `video`. */
      return (
        <div className={styles.embed}>
          <p className={styles.ctaRiga}>
            <a
              href={blocco.url}
              className={styles.cta}
              rel="noopener noreferrer"
              data-external="true"
            >
              {t('block_embed_open', 'Apri il contenuto')}: {blocco.titolo}
            </a>
          </p>
          <p className={styles.attribuzione}>
            {t('render_cammini_fonte', 'Fonte')}: {blocco.fonte}
            {' · '}
            {t(
              'block_embed_external',
              'Contenuto ospitato da un servizio esterno: si apre in una nuova pagina.',
            )}
          </p>
          {blocco.didascalia ? <p className={styles.prosa}>{blocco.didascalia}</p> : null}
        </div>
      );

    default:
      // Tipo non (ancora) supportato da questo frontend: si DICHIARA.
      return (
        <div role="note" className={`${styles.nota} ${styles.notaAvviso}`}>
          <strong>{t('block_warning_prefix', 'Attenzione')}: </strong>
          {t('block_unknown', {
            tipo: (blocco as { tipo?: string }).tipo ?? '?',
            defaultValue: `Questo contenuto (tipo "${(blocco as { tipo?: string }).tipo ?? '?'}") non è ancora visualizzabile: aggiorna la pagina più tardi o segnalalo alla redazione.`,
          })}
        </div>
      );
  }
}
